import { useEffect, useState } from "react";
import { useEditor } from "@tldraw/tldraw";

/**
 * Component that renders a visual overlay showing the selection bounds during resize
 * This ensures the user gets visual feedback during resize operations
 */
export const ResizingOverlay = () => {
  const editor = useEditor();
  const [resizeState, setResizeState] = useState<{
    isResizing: boolean;
    bounds: { x: number; y: number; w: number; h: number } | null;
  }>({
    isResizing: false,
    bounds: null,
  });

  useEffect(() => {
    let lastIsResizing = false;

    const unsubscribe = editor.store.listen(() => {
      try {
        const path = editor.getPath();
        const isInResizeState = path.includes("resizing") || path.includes("pointing_resize_handle");

        if (isInResizeState) {
          // Check if we're resizing a textbox
          const selectedShapeIds = editor.getSelectedShapeIds();

          if (selectedShapeIds.length === 1) {
            const shape = editor.getShape(selectedShapeIds[0]);

            // Only show overlay for customText shapes
            if (shape && shape.type === "customText") {
              const selectionBounds = editor.getSelectionPageBounds();

              if (selectionBounds) {
                const viewportPageBounds = editor.getViewportPageBounds();
                const zoom = editor.getZoomLevel();

                // Convert to screen coordinates
                const screenBounds = {
                  x: (selectionBounds.x - viewportPageBounds.x) * zoom,
                  y: (selectionBounds.y - viewportPageBounds.y) * zoom,
                  w: selectionBounds.w * zoom,
                  h: selectionBounds.h * zoom,
                };

                lastIsResizing = true;
                setResizeState({
                  isResizing: true,
                  bounds: screenBounds,
                });
              }
            }
          }
        } else if (lastIsResizing) {
          // Resize just ended
          lastIsResizing = false;
          setResizeState({
            isResizing: false,
            bounds: null,
          });
        }
      } catch (error) {
        console.error("ResizingOverlay error:", error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [editor]);

  if (!resizeState.isResizing || !resizeState.bounds) {
    return null;
  }

  const { x, y, w, h } = resizeState.bounds;

  return (
    <div
      style={{
        position: "fixed",
        left: x,
        top: y,
        width: w,
        height: h,
        border: "2px solid #338bff",
        pointerEvents: "none",
        zIndex: 10002,
        boxSizing: "border-box",
      }}
    />
  );
};
